import { useEffect, useState } from "react";
import { Instagram, Mail } from "lucide-react";

const links = [
  { id: "reel", label: "Reel" },
  { id: "bts", label: "Behind The Scenes" },
  { id: "about", label: "About" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("reel");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      const sections = links.map((l) => document.getElementById(l.id));
      const y = window.scrollY + window.innerHeight / 3;
      for (let i = sections.length - 1; i >= 0; i--) {
        const s = sections[i];
        if (s && s.offsetTop <= y) {
          setActive(links[i].id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 24;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/5 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="mx-auto max-w-[1600px] px-6 md:px-12 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          data-testid="brand-logo"
          className="group flex items-center gap-3 cursor-pointer"
        >
          <span className="w-2 h-2 rounded-full bg-[#D4AF37] group-hover:scale-125 transition-transform" />
          <span className="font-serif-display text-xl md:text-2xl tracking-tight text-white">
            CinematicLee
          </span>
        </button>

        <ul className="hidden md:flex items-center gap-10">
          {links.map((l, i) => (
            <li key={l.id}>
              <button
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className={`relative font-accent text-[11px] transition-colors ${
                  active === l.id ? "text-[#D4AF37]" : "text-zinc-400 hover:text-white"
                }`}
              >
                <span className="mr-2 text-zinc-600">0{i + 1}</span>
                {l.label}
                {active === l.id && (
                  <span className="absolute -bottom-2 left-0 right-0 mx-auto h-px w-6 bg-[#D4AF37]" />
                )}
              </button>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-4">
          <a
            data-testid="nav-social-instagram"
            href="https://www.instagram.com/leeman09/"
            target="_blank"
            rel="noreferrer"
            className="text-zinc-400 hover:text-[#D4AF37] transition-colors"
            aria-label="Instagram"
          >
            <Instagram size={16} strokeWidth={1.5} />
          </a>
          <a
            data-testid="nav-social-email"
            href="mailto:elfordleeman@gmail.com"
            className="text-zinc-400 hover:text-[#D4AF37] transition-colors"
            aria-label="Email"
          >
            <Mail size={16} strokeWidth={1.5} />
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden flex flex-col gap-1.5 p-2"
          aria-label="Menu"
        >
          <span className={`block h-px w-6 bg-white transition-transform ${open ? "translate-y-[6px] rotate-45" : ""}`} />
          <span className={`block h-px w-6 bg-white transition-opacity ${open ? "opacity-0" : ""}`} />
          <span className={`block h-px w-6 bg-white transition-transform ${open ? "-translate-y-[6px] -rotate-45" : ""}`} />
        </button>
      </nav>

      {open && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/5">
          <ul className="px-6 py-8 flex flex-col gap-6">
            {links.map((l, i) => (
              <li key={l.id}>
                <button
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="font-accent text-sm text-zinc-300 hover:text-[#D4AF37]"
                >
                  <span className="mr-3 text-zinc-600">0{i + 1}</span>
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
}
